import type {
  ResponsesGetReportingFileDisplayParameterResponse,
  ResponsesGetReportingFilesByFilterDocumentResponse,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';

import {
  isFeeSettlementDocumentType,
  isProfitabilityDocumentType,
  type ReportGeneratorDocumentType,
  type ReportGeneratorParameterDefinition,
} from './categories';
import { getReportingFilePeriodLabel } from './periods';
import type { ReportGeneratorDetailParameter } from './types';

export { isFeeSettlementDocumentType, isProfitabilityDocumentType };

const resolveReportGeneratorParameterDisplayValue = (
  definition: ReportGeneratorParameterDefinition | undefined,
  rawValue: string
): string => {
  if (!definition?.options.length) {
    return rawValue;
  }

  const normalizedRawValue = rawValue.trim().toLowerCase();
  const matchedOption = definition.options.find(
    (option) => option.value.trim().toLowerCase() === normalizedRawValue
  );

  return matchedOption?.label?.trim() || rawValue;
};

const findReportGeneratorParameterDefinition = (
  catalogDocumentType: ReportGeneratorDocumentType | undefined,
  parameterName: string | null | undefined
): ReportGeneratorParameterDefinition | undefined => {
  const normalizedName = parameterName?.trim().toLowerCase();
  if (!normalizedName || !catalogDocumentType?.parameters.length) {
    return undefined;
  }

  return catalogDocumentType.parameters.find(
    (parameter) => parameter.name.trim().toLowerCase() === normalizedName
  );
};

const CUSTOMER_DISPLAY_PARAMETER_NAMES = ['customerDisplayName', 'customerName'] as const;

const getDisplayParameter = (
  displayParameters: ResponsesGetReportingFileDisplayParameterResponse[] | null | undefined,
  names: string | readonly string[]
): ResponsesGetReportingFileDisplayParameterResponse | undefined => {
  if (!displayParameters?.length) {
    return undefined;
  }

  const nameList = (Array.isArray(names) ? names : [names]).map((name) => name.toLowerCase());

  return displayParameters.find((parameter) => {
    const parameterName = parameter.name?.trim().toLowerCase();
    return Boolean(parameterName && nameList.includes(parameterName));
  });
};

const getDisplayParameterValue = (
  displayParameters: ResponsesGetReportingFileDisplayParameterResponse[] | null | undefined,
  names: string | readonly string[]
): string | undefined => {
  const value = getDisplayParameter(displayParameters, names)?.value?.trim();
  return value || undefined;
};

export const formatSelectedPeriodDisplay = (
  item: Pick<
    ResponsesGetReportingFilesByFilterDocumentResponse,
    | 'reportingPeriod'
    | 'reportingStartDate'
    | 'reportingEndDate'
    | 'referenceDate'
    | 'documentTypeId'
  >
): string => {
  if (item.reportingPeriod === 'ReferenceDate') {
    if (!item.referenceDate) {
      return '--';
    }

    if (isProfitabilityDocumentType(item.documentTypeId)) {
      return Utils.Mask.DateFormat(item.referenceDate, 'MM/yyyy');
    }

    return Utils.Mask.DateFormat(item.referenceDate, 'dd/MM/yyyy');
  }

  if (item.reportingStartDate && item.reportingEndDate) {
    return `${Utils.Mask.DateFormat(
      item.reportingStartDate,
      'dd/MM/yyyy'
    )} - ${Utils.Mask.DateFormat(item.reportingEndDate, 'dd/MM/yyyy')}`;
  }

  return '--';
};

export const getDocumentTypeSecondaryText = (
  item: Pick<
    ResponsesGetReportingFilesByFilterDocumentResponse,
    'reportingPeriod' | 'displayParameters'
  >,
  options?: { includePeriodLabel?: boolean }
): string | undefined => {
  const customerName = getDisplayParameterValue(
    item.displayParameters,
    CUSTOMER_DISPLAY_PARAMETER_NAMES
  );

  if (customerName) {
    return customerName;
  }

  if (item.displayParameters?.length) {
    return undefined;
  }

  if (options?.includePeriodLabel === false) {
    return undefined;
  }

  const periodLabel = getReportingFilePeriodLabel(item.reportingPeriod);
  return periodLabel === '--' ? undefined : periodLabel;
};

export const buildReportGeneratorDetailParameters = (
  item: Pick<
    ResponsesGetReportingFilesByFilterDocumentResponse,
    'displayParameters' | 'additionalParameters'
  >,
  catalogDocumentType?: ReportGeneratorDocumentType
): ReportGeneratorDetailParameter[] => {
  const fromDisplay =
    item.displayParameters?.reduce<ReportGeneratorDetailParameter[]>((parameters, parameter) => {
      const value = parameter.value?.trim();
      if (!value) {
        return parameters;
      }

      parameters.push({
        label: parameter.label?.trim() || parameter.name?.trim() || 'Parâmetro',
        value: resolveReportGeneratorParameterDisplayValue(
          findReportGeneratorParameterDefinition(catalogDocumentType, parameter.name),
          value
        ),
      });
      return parameters;
    }, []) ?? [];

  if (fromDisplay.length) {
    return fromDisplay;
  }

  const parameterDefinitions = catalogDocumentType?.parameters ?? [];
  const additionalParameters = item.additionalParameters;

  if (!additionalParameters) {
    return [];
  }

  return Object.entries(additionalParameters).reduce<ReportGeneratorDetailParameter[]>(
    (parameters, [name, rawValue]) => {
      const value = rawValue?.trim();
      if (!value) {
        return parameters;
      }

      const definition = parameterDefinitions.find((parameter) => parameter.name === name);
      parameters.push({
        label: definition?.label || name,
        value: resolveReportGeneratorParameterDisplayValue(definition, value),
      });
      return parameters;
    },
    []
  );
};
